import { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setProfileData } from "./redux/slices/profileSlice";
import {
  ensureFreshSession,
  getHomePath,
  getStoredUserInfo,
  hasPersistedSession,
} from "./helper/authSession";
import AuthLayout from "./components/AuthLayout";
import Classes from "./pages/Classes";
import Layout from "./components/Layout";
import ClassDetail from "./pages/ClassDetail";
import SubjectDetail from "./pages/SubjectDetail";
import ProfilePage from "./pages/ProfilePage";
import UserInfo from "./pages/UserInfo";
import EmailPage from "./pages/EmailPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import CreatorPage from "./pages/CreatorPage";
import ChildrenPage from "./pages/ChildrenPage";
import ChildrenDetailPage from "./pages/ChildrenDetailPage";
import WorkshopDetailPage from "./pages/WorkshopDetailPage";
import WorkshopPage from "./pages/WorkshopPage";
import ConfirmPage from "./pages/auth/ConfirmPage";
import LoginPage from "./pages/auth/LoginPage";
import SignupPage from "./pages/auth/SignupPage";

// admin routes
import AuthAdminLayout from "./components/AuthAdminLayout";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./components/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminOtpVerification from "./pages/admin/AdminOtpVerification";
import UserList from "./pages/admin/user/UserList";
import UserDetail from "./pages/admin/user/UserDetail";
import Contentcreator from "./pages/Content-creator";
import ClassList from "./pages/admin/class/ClassList";
import AdminClassDetail from "./pages/admin/class/ClassDetail";
import ParentsList from "./pages/admin/parents/ParentsList";
import TeacherList from "./pages/admin/teacher/TeacherList";
import TeacherDetail from "./pages/admin/teacher/TeacherDetail";
import CreatorList from "./pages/admin/creator/CreatorList";

// public quiz routes
import PublicQuizLayout from "./components/quiz/PublicQuizLayout";
import QuizHome from "./pages/quiz/QuizHome";
import QuizDeckDetail from "./pages/quiz/QuizDeckDetail";
import QuizCardSelection from "./pages/quiz/QuizCardSelection";
import QuizTeacherLobby from "./pages/quiz/QuizTeacherLobby";
import QuizTeacherLive from "./pages/quiz/QuizTeacherLive";
import QuizTeacherResults from "./pages/quiz/QuizTeacherResults";
import QuizStudentJoin from "./pages/quiz/QuizStudentJoin";
import QuizStudentWaiting from "./pages/quiz/QuizStudentWaiting";
import QuizStudentPlay from "./pages/quiz/QuizStudentPlay";
import QuizStudentResults from "./pages/quiz/QuizStudentResults";
// Old quiz pages removed (QuizLobby, QuizPlay, QuizResults)


function PrivateRoute({ children }) {
  return hasPersistedSession() ? children : <Navigate to="/auth/login" replace />;
}

function AdminPrivateRoute({ children }) {
  return hasPersistedSession() ? children : <Navigate to="/admin/login" replace />;
}

function GuestOnly({ children }) {
  return hasPersistedSession() ? (
    <Navigate to={getHomePath()} replace />
  ) : (
    children
  );
}

function RootRedirect() {
  return (
    <Navigate
      to={hasPersistedSession() ? getHomePath() : "/auth/login"}
      replace
    />
  );
}

function SessionBootstrap() {
  const dispatch = useDispatch();

  useEffect(() => {
    const userInfo = getStoredUserInfo();
    if (userInfo) {
      dispatch(setProfileData(userInfo));
    }
    ensureFreshSession();
  }, [dispatch]);

  return null;
}

function App() {
  return (
    <BrowserRouter>
      <SessionBootstrap />
      <Routes>
        {/* Default Redirect — home if already logged in */}
        <Route path="/" element={<RootRedirect />} />

        {/* User Authentication Routes */}
        <Route path="/auth" element={<AuthLayout />}>
          <Route
            path="/auth/login"
            element={
              <GuestOnly>
                <LoginPage />
              </GuestOnly>
            }
          />
          <Route path="/auth/signup" element={<SignupPage />} />
          <Route path="/auth/confirm" element={<ConfirmPage />} />
        </Route>

        {/* User Private Routes */}
        <Route path="/" element={<Layout />}>
          <Route
            path="/classes"
            element={
              <PrivateRoute>
                <Classes />
              </PrivateRoute>
            }
          />
          <Route
            path="/classes/:id"
            element={
              <PrivateRoute>
                <ClassDetail />
              </PrivateRoute>
            }
          />
          <Route
            path="/subject/:id"
            element={
              <PrivateRoute>
                <SubjectDetail />
              </PrivateRoute>
            }
          />
          <Route
            path="/Content-creator"
            element={
              <PrivateRoute>
                <Contentcreator />
              </PrivateRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <ProfilePage />
              </PrivateRoute>
            }
          />
          <Route
            path="/user-info"
            element={
              <PrivateRoute>
                <UserInfo />
              </PrivateRoute>
            }
          />
          <Route
            path="/user-email"
            element={
              <PrivateRoute>
                <EmailPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/change-password"
            element={
              <PrivateRoute>
                <ChangePasswordPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/creator"
            element={
              <PrivateRoute>
                <CreatorPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/mychildren"
            element={
              <PrivateRoute>
                <ChildrenPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/mychildren/:id"
            element={
              <PrivateRoute>
                <ChildrenDetailPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/workshop"
            element={
              <PrivateRoute>
                <WorkshopPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/workshop/:id"
            element={
              <PrivateRoute>
                <WorkshopDetailPage />
              </PrivateRoute>
            }
          />
        </Route>

        {/* Admin Authentication Routes */}
        <Route path="/admin/auth" element={<AuthAdminLayout />}>
          <Route path="login" element={<AdminLogin />} />
          <Route path="otp-verification" element={<AdminOtpVerification />} />
        </Route>

        {/* Admin Private Routes */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route path="dashboard" element={<AdminPrivateRoute><AdminDashboard /></AdminPrivateRoute>} />

          <Route path="userlist" element={<AdminPrivateRoute><UserList /></AdminPrivateRoute>} />
          <Route path="userlist/:id" element={<AdminPrivateRoute><UserDetail /></AdminPrivateRoute>} />

          <Route path="classlist" element={<AdminPrivateRoute><ClassList /></AdminPrivateRoute>} />
          <Route path="classdetail/:id" element={<AdminPrivateRoute><AdminClassDetail /></AdminPrivateRoute>} />

          <Route path="parentslist" element={<AdminPrivateRoute><ParentsList /></AdminPrivateRoute>} />

          <Route path="teacherlist" element={<AdminPrivateRoute><TeacherList /></AdminPrivateRoute>} />
          <Route path="teacherdetail/:id" element={<AdminPrivateRoute><TeacherDetail /></AdminPrivateRoute>} />

          <Route path="creatorList" element={<AdminPrivateRoute><CreatorList /></AdminPrivateRoute>} />

        </Route>

        {/* Public Quiz Routes (no auth required) */}
        <Route path="/quiz" element={<PublicQuizLayout />}>
          <Route index element={<QuizHome />} />
          <Route path="deck/:deckId" element={<QuizDeckDetail />} />
          <Route path="deck/:deckId/select-cards" element={<QuizCardSelection />} />
          {/* Teacher quiz session */}
          <Route path="session/:sessionId/teacher" element={<QuizTeacherLobby />} />
          <Route path="session/:sessionId/teacher/live" element={<QuizTeacherLive />} />
          <Route path="session/:sessionId/teacher/results" element={<QuizTeacherResults />} />
          {/* Student quiz session */}
          <Route path="join/:sessionId" element={<QuizStudentJoin />} />
          <Route path="session/:sessionId/waiting" element={<QuizStudentWaiting />} />
          <Route path="session/:sessionId/play" element={<QuizStudentPlay />} />
          <Route path="session/:sessionId/results" element={<QuizStudentResults />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
